import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const S = {
  sidebar: {
    width: '200px',
    minWidth: '200px',
    maxWidth: '200px',
    height: '100vh',
    background: '#161616',
    borderRight: '1px solid #2a2a2a',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Trebuchet MS', system-ui, sans-serif",
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 12px 10px',
    borderBottom: '1px solid #232323',
    flexShrink: 0,
  },
  headerTitle: {
    color: '#e2bf6d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  collapseBtn: {
    background: 'none',
    border: 'none',
    color: '#555',
    fontSize: 16,
    cursor: 'pointer',
    padding: '2px 4px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },
  newChatBtn: {
    margin: '10px 10px 6px',
    padding: '8px 12px',
    background: 'transparent',
    border: '1px solid #3a3a3a',
    borderRadius: 7,
    color: '#ccc',
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Trebuchet MS', system-ui, sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s',
  },
  searchContainer: {
    position: 'relative',
    margin: '4px 10px 8px',
  },
  searchInput: {
    width: '100%',
    padding: '6px 24px 6px 8px',
    background: '#1e1e1e',
    border: '1px solid #333',
    borderRadius: 6,
    color: '#aaa',
    fontSize: 11,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Trebuchet MS', system-ui, sans-serif",
  },
  clearSearch: {
    position: 'absolute',
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#555',
    cursor: 'pointer',
    fontSize: 10,
    padding: '2px',
    lineHeight: 1,
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 4px',
  },
  groupLabel: {
    padding: '8px 9px 3px',
    color: '#444',
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  convItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '7px 9px',
    borderRadius: 6,
    cursor: 'pointer',
    margin: '1px 2px',
    minHeight: 32,
    transition: 'background 0.1s',
    gap: 4,
  },
  convTitle: {
    color: '#aaa',
    fontSize: 12,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  editInput: {
    flex: 1,
    background: '#2a2a2a',
    border: '1px solid #444',
    borderRadius: 4,
    color: '#fff',
    fontSize: 11,
    padding: '2px 5px',
    outline: 'none',
    fontFamily: "'Trebuchet MS', system-ui, sans-serif",
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    gap: 1,
    flexShrink: 0,
  },
  actionBtn: {
    background: 'none',
    border: 'none',
    color: '#555',
    fontSize: 12,
    cursor: 'pointer',
    padding: '2px 3px',
    borderRadius: 3,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },
  emptyState: {
    color: '#444',
    fontSize: 11,
    textAlign: 'center',
    padding: '20px 10px',
    lineHeight: 1.6,
  },
  footer: {
    padding: '8px 10px',
    borderTop: '1px solid #222',
    flexShrink: 0,
  },
  logoutBtn: {
    width: '100%',
    padding: '7px 10px',
    background: 'none',
    border: '1px solid #2a2a2a',
    borderRadius: 6,
    color: '#555',
    fontSize: 11,
    cursor: 'pointer',
    fontFamily: "'Trebuchet MS', system-ui, sans-serif",
    transition: 'all 0.15s',
  },
};

export default function ConversationSidebar({
  token, currentConversationId, onConversationSelect,
  onNewChat, onLogout, casinoId, onToggle
}) {
  const [conversations,   setConversations]   = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchResults,   setSearchResults]   = useState(null);
  const [hoveredId,       setHoveredId]       = useState(null);
  const [editingId,       setEditingId]       = useState(null);
  const [editTitle,       setEditTitle]       = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => { if (token) loadConversations(); }, [token]);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchQuery.trim().length >= 2) {
      searchTimeout.current = setTimeout(() => searchConversations(searchQuery), 400);
    } else {
      setSearchResults(null);
    }
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setConversations((await res.json()).conversations || []);
    } catch {}
    finally { setLoading(false); }
  };

  const searchConversations = async (query) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/conversations/search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setSearchResults((await res.json()).conversations || []);
    } catch {}
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (deleteConfirmId !== id) { setDeleteConfirmId(id); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConversations(prev => prev.filter(c => c.conversation_id !== id));
        if (currentConversationId === id) onNewChat();
      }
    } catch {}
    finally { setDeleteConfirmId(null); }
  };

  const handleRename = async (id) => {
    if (!editTitle.trim()) { setEditingId(null); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/conversations/${id}/title`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle.trim() })
      });
      if (res.ok) {
        setConversations(prev => prev.map(c =>
          c.conversation_id === id ? { ...c, title: editTitle.trim() } : c
        ));
      }
    } catch {}
    finally { setEditingId(null); setEditTitle(''); }
  };

  const startEdit = (conv, e) => {
    e.stopPropagation();
    setEditingId(conv.conversation_id);
    setEditTitle(conv.title);
    setDeleteConfirmId(null);
  };

  const groupConversations = (list) => {
    const order = ['Today', 'Yesterday', 'Last 7 days', 'Older'];
    const groups = {};
    order.forEach(g => { groups[g] = []; });
    list.forEach(conv => {
      const g = conv.date_group || 'Older';
      if (!groups[g]) groups[g] = [];
      groups[g].push(conv);
    });
    return order.map(g => ({ group: g, items: groups[g] })).filter(g => g.items.length > 0);
  };

  const displayList = searchResults !== null ? searchResults : conversations;
  const grouped     = groupConversations(displayList);

  return (
    <div style={S.sidebar}>

      {/* Header */}
      <div style={S.header}>
        <span style={S.headerTitle}>History</span>
        <button onClick={onToggle} style={S.collapseBtn} title="Collapse sidebar">
          ←
        </button>
      </div>

      {/* New Chat */}
      <button
        onClick={onNewChat}
        style={S.newChatBtn}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#bca97a'; e.currentTarget.style.color = '#e2bf6d'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#3a3a3a'; e.currentTarget.style.color = '#ccc'; }}
      >
        <span style={{ fontSize: 14 }}>+</span> New Chat
      </button>

      {/* Search */}
      <div style={S.searchContainer}>
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} style={S.clearSearch}>✕</button>
        )}
      </div>

      {/* List */}
      <div style={S.listContainer}>
        {loading ? (
          <div style={S.emptyState}>Loading...</div>
        ) : displayList.length === 0 ? (
          <div style={S.emptyState}>
            {searchQuery ? 'No results' : 'No conversations yet.\nStart a new chat!'}
          </div>
        ) : (
          grouped.map(({ group, items }) => (
            <div key={group}>
              <div style={S.groupLabel}>{group}</div>
              {items.map(conv => {
                const isActive  = currentConversationId === conv.conversation_id;
                const isHovered = hoveredId === conv.conversation_id;
                const isEditing = editingId === conv.conversation_id;
                return (
                  <div
                    key={conv.conversation_id}
                    onClick={() => { if (!isEditing && deleteConfirmId !== conv.conversation_id) onConversationSelect(conv.conversation_id); }}
                    onMouseEnter={() => setHoveredId(conv.conversation_id)}
                    onMouseLeave={() => { setHoveredId(null); setDeleteConfirmId(null); }}
                    style={{
                      ...S.convItem,
                      background: isActive ? '#2d2d2d' : isHovered ? '#212121' : 'transparent',
                      borderLeft: isActive ? '2px solid #bca97a' : '2px solid transparent',
                    }}
                  >
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onBlur={() => handleRename(conv.conversation_id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter')  handleRename(conv.conversation_id);
                          if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
                        }}
                        onClick={e => e.stopPropagation()}
                        style={S.editInput}
                      />
                    ) : (
                      <span
                        style={{ ...S.convTitle, color: isActive ? '#e2bf6d' : '#999' }}
                        onDoubleClick={e => startEdit(conv, e)}
                        title={conv.title}
                      >
                        {conv.title || 'Untitled'}
                      </span>
                    )}

                    {isHovered && !isEditing && (
                      <div style={S.actions} onClick={e => e.stopPropagation()}>
                        <button onClick={e => startEdit(conv, e)} style={S.actionBtn} title="Rename">✎</button>
                        <button
                          onClick={e => handleDelete(conv.conversation_id, e)}
                          style={{
                            ...S.actionBtn,
                            color: deleteConfirmId === conv.conversation_id ? '#e55' : '#555',
                          }}
                          title={deleteConfirmId === conv.conversation_id ? 'Confirm?' : 'Delete'}
                        >
                          {deleteConfirmId === conv.conversation_id ? '✓' : '×'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <button
          onClick={onLogout}
          style={S.logoutBtn}
          onMouseEnter={e => { e.currentTarget.style.color = '#e55'; e.currentTarget.style.borderColor = '#5a2020'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}